import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { createContext, ReactNode, useEffect } from "react";
import { ENV_VARS, getCurrentEnv, isProduction } from "../../../config/env";

interface PostHogContextType {
	posthog: typeof posthog;
}

const PostHogContext = createContext<PostHogContextType | null>(null);

interface PostHogProviderProps {
	children: ReactNode;
}

export const PostHogProvider = ({ children }: PostHogProviderProps) => {
	useEffect(() => {
		const initPostHog = () => {
			if (!ENV_VARS.VITE_PUBLIC_POSTHOG_KEY) {
				console.log("PostHogProvider: No API key found, skipping initialization");
				return;
			}

			// Only initialize PostHog in the production environment
			if (!isProduction()) {
				console.log(`PostHogProvider: Skipping initialization in ${getCurrentEnv()} environment`);
				return;
			}

			try {
				posthog.init(ENV_VARS.VITE_PUBLIC_POSTHOG_KEY, {
					api_host: "https://eu.i.posthog.com",
					person_profiles: "identified_only",
					capture_pageview: true,
					capture_pageleave: true,
					disable_session_recording: true,
					capture_performance: false,
					loaded: (_posthog: any) => {
						console.log(`PostHogProvider: initialized in ${getCurrentEnv()} environment`);
					},
					// Filter out Bria org users to maximize free credits (temporarily disabled)
					before_send: (event) => {
						if (!event) return null;

						const BRIA_ORG_DOMAINS = ["@bria.ai"];
						const isBriaUser = (email: string | null | undefined): boolean => {
							if (!email) return false;
							return BRIA_ORG_DOMAINS.some((domain) => email.toLowerCase().includes(domain));
						};

						if (isBriaUser(posthog.get_distinct_id()) || isBriaUser(event.properties?.email)) {
							console.log("PostHog: User is internal, skipping event");
							return null;
						}

						return event;
					},
				});
			} catch (error) {
				console.error("PostHogProvider: Initialization failed", error);
				Sentry.captureException(error);
			}
		};

		initPostHog();
	}, []);

	return <PostHogContext.Provider value={{ posthog }}>{children}</PostHogContext.Provider>;
};
